import { OverscopeDetectedCard } from "./dashboard/OverscopeDetectedCard";
import { AdditionalItemsCard } from "./dashboard/AdditionalItemsCard";
import { PriceDiscrepancyCard } from "./dashboard/PriceDiscrepancyCard";
import { OmittedItemsCard } from "./dashboard/OmittedItemsCard";
import { FinancialIntelligenceReport } from "./dashboard/FinancialIntelligenceReport";
import type { SummaryStats, EnhancedRoomMatch } from "./types";

interface SummaryDashboardProps {
    stats: SummaryStats;
    enhancedMatches: EnhancedRoomMatch[];
    unmatchedLineItemsA?: any[];
    unmatchedLineItemsB?: any[];
    extractionA?: any;
    extractionB?: any;
    onNavigateToMatch: (index: number) => void;
    onViewOverscopedItems?: () => void;
    onViewAdditionalItems?: () => void;
    onViewUnderscopedItems?: () => void;
    onViewOmittedItems?: () => void;
    // Optional unused props kept for compatibility if needed
    onAcceptOverscopedItems?: () => void;
    onAddAllToSupplement?: () => void;
    onAcceptAdditionalItems?: () => void;
}

export default function SummaryDashboard({
    stats,
    unmatchedLineItemsA = [],
    unmatchedLineItemsB = [],
    onViewOverscopedItems,
    onViewAdditionalItems,
    onViewUnderscopedItems,
    onViewOmittedItems
}: SummaryDashboardProps) {
    // Calculate carrier pre-approved items (unmatched insurance line items) - include all items, even those with RCV <= 0
    const carrierPreApprovedItems = unmatchedLineItemsA;
    const carrierPreApprovedTotal = carrierPreApprovedItems.reduce((sum, item) => sum + (parseFloat(String(item.rcv)) || 0), 0);

    // Calculate underscoped items (contractor bid > insurance estimate)
    const underscopedItems = stats.contractorOverscopeItems || [];
    const underscopedTotal = stats.contractorOverscope || 0;

    // Calculate omitted items (unmatched contractor items) - include all items, even those with RCV <= 0
    const omittedItems = unmatchedLineItemsB;
    const omittedTotal = omittedItems.reduce((sum, item) => sum + (parseFloat(String(item.rcv)) || 0), 0);

    return (
        <div className="space-y-4">
            {/* First Row - Overscope and AI-Suggested Items */}
            <div className="grid grid-cols-1 xl:grid-cols-2 gap-4">
                <OverscopeDetectedCard
                    stats={stats}
                    onViewOverscopedItems={onViewOverscopedItems}
                />

                <AdditionalItemsCard
                    itemsCount={carrierPreApprovedItems.length}
                    totalValue={carrierPreApprovedTotal}
                    onViewAdditionalItems={onViewAdditionalItems}
                />
            </div>

            {/* Second Row - Underscoped and Omitted Items */}
            <div className="grid grid-cols-1 xl:grid-cols-2 gap-4">
                <PriceDiscrepancyCard
                    itemsCount={underscopedItems.length}
                    totalDiscrepancy={underscopedTotal}
                    onViewUnderscopedItems={onViewUnderscopedItems}
                />

                <OmittedItemsCard
                    itemsCount={omittedItems.length}
                    totalValue={omittedTotal}
                    onViewOmittedItems={onViewOmittedItems}
                />
            </div>

            {/* Financial Intelligence Report */}
            <FinancialIntelligenceReport
                stats={stats}
                carrierPreApprovedTotal={carrierPreApprovedTotal}
                carrierPreApprovedCount={carrierPreApprovedItems.length}
            />
        </div>
    );
}
